import React, { useState, useRef, useEffect } from 'react';

import { InView } from 'react-intersection-observer';
import { ILazyProps } from './types';

function usePrevious<T>(value: T) {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export const Lazy: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<ILazyProps>
>> = ({
  className = undefined,
  children = null,
  placeholder = null,
  offset = 100,
  loadOnIdle = false,
}) => {
  const [shouldLoad, setShouldLoad] = useState<boolean>(
    () => process.env.NODE_ENV === 'test'
  );
  const [abortIdle, setAbortIdle] = useState<Function | null>(null);

  const previousLoadOnIdle = usePrevious(loadOnIdle);

  useEffect(() => {
    if (loadOnIdle && shouldLoad) {
      scheduleLoadOnIdle();
    }

    return () => {
      if (abortIdle) {
        abortIdle();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!previousLoadOnIdle && loadOnIdle && !shouldLoad) {
      scheduleLoadOnIdle();
    }
  });

  function load() {
    setShouldLoad(true);
    if (abortIdle) {
      abortIdle();
    }
  }

  function scheduleLoadOnIdle() {
    if (window && window.requestIdleCallback) {
      const idleHandler = window.requestIdleCallback(load);
      setAbortIdle(() => {
        window.cancelIdleCallback(idleHandler);
        setAbortIdle(null);
      });
    } else {
      const timeoutHandler = setTimeout(load, 100);
      setAbortIdle(() => {
        clearTimeout(timeoutHandler);
        setAbortIdle(null);
      });
    }
  }

  if (shouldLoad) {
    return <>{children}</>;
  }

  function onChangeHandler(inView: boolean, entry: IntersectionObserverEntry) {
    if (!shouldLoad && isInView(inView, entry)) {
      load();
    }
  }

  return (
    <InView rootMargin={`${offset}px`} onChange={onChangeHandler}>
      {({ ref }) => {
        return (
          <div ref={ref} className={className}>
            {placeholder}
          </div>
        );
      }}
    </InView>
  );
};

function isInView(
  inView: boolean,
  entry: IntersectionObserverEntry | undefined
) {
  return inView || (entry && entry.intersectionRatio > 0);
}
